import { forwardRef } from "react";

import { cva, type VariantProps } from "class-variance-authority";

import * as RadixDialog from "@radix-ui/react-dialog";
import * as VisuallyHidden from "@radix-ui/react-visually-hidden";

import { cn } from "../index";

/* -------------------------------------------------------------------------------------------------
 * Sheet
 * -----------------------------------------------------------------------------------------------*/

type SheetProps = RadixDialog.DialogProps;

const Root = RadixDialog.Root;

Root.displayName = "Sheet";

/* -------------------------------------------------------------------------------------------------
 * Trigger
 * -----------------------------------------------------------------------------------------------*/

type TriggerProps = RadixDialog.DialogTriggerProps;

const Trigger = RadixDialog.Trigger;

Trigger.displayName = "SheetTrigger";

/* -------------------------------------------------------------------------------------------------
 * Overlay
 * -----------------------------------------------------------------------------------------------*/

type PortalProps = RadixDialog.DialogPortalProps;

const Portal = RadixDialog.Portal;

Portal.displayName = "SheetPortal";

/* -------------------------------------------------------------------------------------------------
 * Overlay
 * -----------------------------------------------------------------------------------------------*/

type OverlayProps = RadixDialog.DialogOverlayProps;

const Overlay = forwardRef<HTMLDivElement, OverlayProps>((props, forwardedRef) => {
  const { className, ...overlayProps } = props;
  return (
    <RadixDialog.Overlay
      {...overlayProps}
      ref={forwardedRef}
      className={cn("sheet-overlay inset-0 md:bg-primary-dark-blue/40 fixed z-50 bg-transparent", className)}
    />
  );
});

Overlay.displayName = "SheetOverlay";

/* -------------------------------------------------------------------------------------------------
 * Content
 * -----------------------------------------------------------------------------------------------*/

const sheetVariants = cva(
  "sheet md:m-0 md:rounded-none fixed z-50 m-[10px] overflow-hidden rounded-[10px] bg-primary-white shadow-[var(--shadow-elevated)]",
  {
    variants: {
      side: {
        top: "sheet_top top-0 inset-x-0",
        right:
          "sheet_right inset-y-0 right-0 md:max-w-[500px] md:h-full h-[calc(100%-20px)] w-[75%] max-w-[min(80dvw,500px)]",
        bottom: "sheet_bottom bottom-0 inset-x-0",
        left: "sheet_left inset-y-0 left-0 md:max-w-[500px] md:h-full h-[calc(100%-20px)] w-[75%] max-w-[80dvw]",
      },
    },
    defaultVariants: {
      side: "right",
    },
  },
);

interface ContentProps extends RadixDialog.DialogContentProps, VariantProps<typeof sheetVariants> {}

const Content = forwardRef<HTMLDivElement, ContentProps>((props, forwardedRef) => {
  const { className, side, children, ...contentProps } = props;
  return (
    <RadixDialog.Content
      {...contentProps}
      ref={forwardedRef}
      className={sheetVariants({ side, className })}
      aria-describedby={undefined}
    >
      <VisuallyHidden.Root>
        <RadixDialog.Title />
      </VisuallyHidden.Root>
      {children}
    </RadixDialog.Content>
  );
});

Content.displayName = "SheetContent";

/* -------------------------------------------------------------------------------------------------
 * Close
 * -----------------------------------------------------------------------------------------------*/

type CloseProps = RadixDialog.DialogCloseProps;

const Close = RadixDialog.Close;

Close.displayName = "SheetClose";

export { Close, Content, Overlay, Portal, Root, Trigger };
export type { CloseProps, ContentProps, OverlayProps, PortalProps, SheetProps, TriggerProps };
